import './TextViewerWrapper.scss';

import TextViewer from '@ad-platform/tm-rule-text-viewer';
import React, { useEffect, useMemo, useState } from 'react';

import type { TargetingRule } from '../../../apis/graphql';
import { useFetchDecoratedTargetingRule } from '../../../apis/targetingManagerApi/targetingManagerApi';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { EntityType } from '../../../pages/Trafficking/TraffickingPage/hooks/makePageDrawerPlugin';
import bem from '../../../utils/bem';
import Loader from '../../Loader';
import ToggleSwitch from '../../ToggleSwitch';

const [block] = bem('text-viewer-wrapper');

interface Props {
  entityType: EntityType.AD | EntityType.LINE_ITEM;
  adRule?: TargetingRule | null;
  lineItemRule?: TargetingRule | null;
}

const AD_RULE_ERROR_MESSAGE = 'Failed to translate Ad rule. Ad rule text view is unavailable';
const LINE_ITEM_RULE_ERROR_MESSAGE = 'Failed to translate Line Item rule. Line Item rule text view is unavailable';

function TextViewWrapper({ entityType, adRule, lineItemRule }: Readonly<Props>): JSX.Element {
  const [preset, setPreset] = useState(false);
  const [adFetchError, setAdFetchError] = useState<string | null>(null);
  const [lineItemFetchError, setLineItemFetchError] = useState<string | null>(null);

  const {
    adsMissionControlTargetingTextViewerInitialValuesListTruncateLength: initialValuesListTruncateLength,
    adsMissionControlTargetingTextViewerValuesListShowMoreSize: valuesListShowMoreSize,
  } = useFeatureFlags();

  // Build the `options` object only from flags that have a positive configured
  // value. LaunchDarkly defaults the integer flags to 0 (or undefined when
  // missing) -- in those cases we omit the field entirely so the TextViewer
  // falls back to its own internal defaults rather than forcing a 0-length
  // truncation.
  const textViewerOptions = useMemo(() => {
    const options: { initialValuesListTruncateLength?: number; valuesListShowMoreSize?: number } = {};
    if (typeof initialValuesListTruncateLength === 'number' && initialValuesListTruncateLength > 0) {
      options.initialValuesListTruncateLength = initialValuesListTruncateLength;
    }
    if (typeof valuesListShowMoreSize === 'number' && valuesListShowMoreSize > 0) {
      options.valuesListShowMoreSize = valuesListShowMoreSize;
    }
    return options;
  }, [initialValuesListTruncateLength, valuesListShowMoreSize]);

  const isAdEntity = entityType === EntityType.AD;

  // Only fetch the ad rule when the entity is an Ad; line item rule is always
  // fetched (when provided) because both Ad and Line Item text views render
  // the decorated line item rule.
  const adRuleToFetch = isAdEntity && !adFetchError ? adRule ?? null : null;
  const lineItemRuleToFetch = !lineItemFetchError ? lineItemRule ?? null : null;

  const { data: decoratedAdRule, loading: adLoading, error: adError } = useFetchDecoratedTargetingRule(
    adRuleToFetch,
    preset
  );
  const {
    data: decoratedLineItemRule,
    loading: lineItemLoading,
    error: lineItemError,
  } = useFetchDecoratedTargetingRule(lineItemRuleToFetch, preset);

  useEffect(() => {
    setAdFetchError(null);
  }, [adRule]);

  useEffect(() => {
    setLineItemFetchError(null);
  }, [lineItemRule]);

  useEffect(() => {
    if (adError && !adFetchError) {
      setAdFetchError(AD_RULE_ERROR_MESSAGE);
    }
  }, [adError, adFetchError]);

  useEffect(() => {
    if (lineItemError && !lineItemFetchError) {
      setLineItemFetchError(LINE_ITEM_RULE_ERROR_MESSAGE);
    }
  }, [lineItemError, lineItemFetchError]);

  const isLoading = adLoading || lineItemLoading;
  const hasData = isAdEntity
    ? Boolean(decoratedAdRule) || Boolean(decoratedLineItemRule)
    : Boolean(decoratedLineItemRule);

  const errorMessages: string[] = [];
  if (isAdEntity && adFetchError) {
    errorMessages.push(adFetchError);
  }
  if (lineItemFetchError) {
    errorMessages.push(lineItemFetchError);
  }

  if (isLoading) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  if (!hasData && errorMessages.length === 0) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  return (
    <div>
      {hasData && (
        <div className={block()}>
          <ToggleSwitch
            isHorizontalPositioning
            isOn={preset}
            onChange={(): void => setPreset((prev) => !prev)}
            label="Preview with preset terms"
          />
          <TextViewer
            isAdEntityType={isAdEntity}
            adRule={isAdEntity ? decoratedAdRule : undefined}
            lineItemRule={decoratedLineItemRule}
            fileName={'targetingRule.pdf'}
            options={textViewerOptions}
          />
        </div>
      )}
      {errorMessages.length > 0 && (
        <div className={block('error')}>
          {errorMessages.map((msg) => (
            <p key={msg}>{msg}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export default TextViewWrapper;
